import { Injectable, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, tap } from 'rxjs';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';

import { MenuChoiceDto, MenuItemDto } from '../../services/menu/dto/api-response-dto';
import { ProductModalChoicesComponent } from '../../pages/store/menu/product-modal-choices/product-modal-choices.component';
import { ProductModalDefaultComponent } from '../../pages/store/menu/product-modal-default/product-modal-default.component';
import { TableApiService } from '../../services/tables/table-api.service';
import { ApiResponseDto } from '../../services/tables/dto/api-response-dto';
import { SocketService } from '../../services/socket/socket.service';
import { LocalStorageService } from '../../services/local-storage.service';

import { TableService } from './table.service';

export interface SubmitOrderParams {
  tableId: number;
  clientId: number;
  idi: number;
  /** users.id do payment-table */
  userId?: number;
}

export interface MenuItemModalData {
  product: MenuItemDto;
  quantity: number;
  choices?: MenuChoiceDto[];
  cartItemId?: string; // Optional: indicates edit mode when present
  additional_info?: string; // Optional: user observations/notes
}

@Injectable({
  providedIn: 'root',
})
export class CartService {

  cart: any[] = [];
  cartSubject: Subject<any> = new Subject<any>();
  cart$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  menuUrl: string = '';

  // Scroll da página capturado ao abrir o modal de produto, antes do ngx-smart-modal
  // aplicar body.dialog-open{overflow:hidden} (que zera o window.scrollY). Usado pelo
  // modal pra restaurar a posição ao fechar.
  savedScrollPosition: number = 0;

  qtyItemsAddedToCartSubject: Subject<number> = new Subject<number>();
  qtyItemsAddedToCart$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(
    private tableService: TableService,
    private ngxSmartModalService: NgxSmartModalService,
    private router: Router,
    private tableApiService: TableApiService,
    private socketService: SocketService,
    private localStorageService: LocalStorageService,
  ) {
    this.menuUrl = this.tableService.getMenuUrl() || '';
    this.cart = this.cart$.getValue();

  }

  getQtyItemsAddedToCart(): number {
    return this.qtyItemsAddedToCart$.getValue();
  }

  setQtyItemsAddedToCart(qty: number): void {
    this.qtyItemsAddedToCart$.next(qty);
  }

  getCart(): any[] {
    return this.cart;
  }

  setCart(cart: any[]): void {
    this.cart = cart;
    this.cartSubject.next(cart);
    this.cart$.next(cart);
  }

  setAdditionalInfo(itemId: number, additionalInfo: string): void {
    const item = this.cart.find((item) => item.id === itemId);
    if (item) {
      item.additional_info = additionalInfo;
    }
  }

  addItemToCart(item: any, qty?: number, choices?: any[], total?: number, additional_info?: string): void {
    const itemHasChoices: boolean = item.choices && item.choices.length > 0;
    const modalId = itemHasChoices ? 'product-modal-with-choices' : 'product-modal-default';
    const productModal: NgxSmartModalComponent | undefined = this.ngxSmartModalService.getModal(modalId) || undefined;
    const itemTotal = total || (item.promotional_price ? item.promotional_price : item.price);
    const cartItemId = crypto.randomUUID();

    const newItem = itemHasChoices
      ? {
          ...item,
          cartItemId,
          qty: Number(qty) || 1,
          availableChoices: item.choices || [], // Store original
          selectedChoices: choices || [],        // Store selections
          total: itemTotal,
          additional_info: additional_info || ''
        }
      : { ...item, cartItemId, qty: Number(qty) || 1, total: itemTotal, additional_info: additional_info || '' };

    // Remove the choices property from items with choices to avoid confusion
    if (itemHasChoices && newItem.choices) {
      delete newItem.choices;
    }
    // Check if item with same product and choices already exists (consolidate duplicates)
    const existingItemIndex = this.findDuplicateItem(newItem);
    if (existingItemIndex >= 0) {
      this.cart[existingItemIndex].qty += newItem.qty;
    } else {
      // Add as new item
      this.cart.push(newItem);
    }

    this.setCart(this.cart);
    this.cart$.next(this.cart);

    if (productModal !== undefined) {
      productModal?.close();
    }

    this.setQtyItemsAddedToCart(this.getQtyItemsAddedToCart() + Number(qty) || 1);
  }

  removeItemFromCart(cartItemId: string): void {
    const itemIndex = this.cart.findIndex((item) => item.cartItemId === cartItemId);
    if (itemIndex === -1) {
      console.error('[CartService] Item not found for removal:', cartItemId);
      return;
    }
    this.cart.splice(itemIndex, 1);
    this.setCart(this.cart);
    this.cart$.next(this.cart);
    return;
  }

  navigateToCart(): void {
    this.router.navigate([`${this.menuUrl}/pedido`], { replaceUrl: true });
  }

  clearCart(): void {
    this.cart = [];
    this.setCart(this.cart);
    this.setQtyItemsAddedToCart(0);
  }

  getCartTotal(): number {
    return this.cart.reduce((total: number, item: any) => total + item.total * item.qty, 0);
  }

  /**
   * Submit the current cart as an order.
   * On success, emits socket events, reloads the table and clears the cart.
   * @param params tableId, clientId (0 when not logged), idi and userId
   */
  submitOrder(params: SubmitOrderParams): Observable<ApiResponseDto> {
    const uuid = crypto.randomUUID();
    const transformedItems = this.transformCartItems(this.cart);

    const payload = {
      uuid,
      total: this.cart.reduce((acc, item) => acc + (item.total || item.price) * item.qty, 0),
      clientId: params.clientId,
      userId: params.userId,
      items: transformedItems,
      idi: params.idi,
    };

    return this.tableApiService.submitCartItems(params.tableId, payload as any).pipe(
      tap(() => {
        this.socketService.emit(
          'bot:table',
          JSON.stringify({
            idi: `${localStorage.getItem('idi')}`,
          }),
        );
        this.socketService.emit('kds:update', 'open');
        this.tableService.loadTable(this.localStorageService.getItem('table_uuid') || '', true);
        this.clearCart();
      }),
    );
  }

  /**
   * Clean cart items for the API payload: removes internal-only fields and
   * renames selectedChoices to choices.
   */
  private transformCartItems(items: any[]): any[] {
    const fieldsToRemove = [
      'img',
      'ifood_promotional_price',
      'ifood_price',
      'promotional_percentage',
      'schedule_type',
      'schedule_available',
      'updated_at',
      'order',
      'minPriceComplements',
      'integration_code',
      'created_at',
      'deleted_at',
      'description',
      'availableChoices',
    ];

    const cleanObject = (obj: any): any => {
      if (!obj || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => cleanObject(item));
      }

      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && !fieldsToRemove.includes(key)) {
          if (key === 'selectedChoices' && obj[key]) {
            cleaned['choices'] = obj[key].map((item: any) => {
              const cleanedChoice = cleanObject(item);
              if (cleanedChoice.total !== undefined) {
                cleanedChoice.price = cleanedChoice.total;
                delete cleanedChoice.total;
              }
              return cleanedChoice;
            });
          } else if (key === 'choices' || key === 'options') {
            cleaned[key] = obj[key].map((item: any) => {
              const cleanedChoice = cleanObject(item);
              if (cleanedChoice.total !== undefined) {
                cleanedChoice.price = cleanedChoice.total;
                delete cleanedChoice.total;
              }
              return cleanedChoice;
            });
          } else if (key === 'origin') {
            cleaned[key] = cleanObject(obj[key]);
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            cleaned[key] = cleanObject(obj[key]);
          } else {
            cleaned[key] = obj[key];
          }
        }
      }
      return cleaned;
    };

    return items.map(item => cleanObject(item));
  }

  /**
   * Update an existing cart item
   * @param cartItemId Unique identifier of the cart item to update
   * @param qty New quantity
   * @param choices New choices (for items with choices)
   * @param total New total price
   * @param additional_info User observations/notes
   */
  updateCartItem(cartItemId: string, qty: number, choices?: any[], total?: number, additional_info?: string): void {
    const itemIndex = this.cart.findIndex((item) => item.cartItemId === cartItemId);

    if (itemIndex === -1) {
      console.error('[CartService] Item not found for update:', cartItemId);
      return;
    }

    const currentItem = this.cart[itemIndex];
    const modalId = currentItem.selectedChoices || currentItem.availableChoices
      ? 'product-modal-with-choices'
      : 'product-modal-default';

    const productModal: NgxSmartModalComponent | undefined = this.ngxSmartModalService.getModal(modalId) ?? undefined;

    // Calculate quantity difference for cart badge update
    const qtyDifference = qty - currentItem.qty;

    // Update the item
    this.cart[itemIndex] = {
      ...currentItem,
      qty: Number(qty),
      selectedChoices: choices || currentItem.selectedChoices,
      total: total || currentItem.total,
      additional_info: additional_info !== undefined ? additional_info : currentItem.additional_info
    };

    this.setCart(this.cart);
    this.cart$.next(this.cart);

    if (productModal !== undefined) {
      productModal?.close();
    }

    // Update cart badge count
    if (qtyDifference !== 0) {
      this.setQtyItemsAddedToCart(this.getQtyItemsAddedToCart() + qtyDifference);
    }
  }

  /**
   * Edit the quantity of an item in the cart
   * @note Can be used outside product
   * @param cartItemId Unique identifier of the cart item to update
   * @param qty New quantity
   */
  editItemQuantity(cartItemId: string, qty: number): void {
    // Se quantidade é zero ou negativa, remove o item
    if (qty <= 0) {
      this.removeItemFromCart(cartItemId);
      return;
    }

    const itemIndex = this.cart.findIndex((item) => item.cartItemId === cartItemId);

    if (itemIndex === -1) {
      console.error('[CartService] Item not found for update:', cartItemId);
      return;
    }

    const currentItem = this.cart[itemIndex];
    const qtyDifference = qty - currentItem.qty;

    // Update the item
    this.cart[itemIndex] = {
      ...currentItem,
      qty: Number(qty),
    };

    this.setCart(this.cart);
    this.cart$.next(this.cart);
    
    // Update cart badge count
    if (qtyDifference !== 0) {
      this.setQtyItemsAddedToCart(this.getQtyItemsAddedToCart() + qtyDifference);
    }
  }

  /**
   * Find if an item with the same product and choices already exists in cart
   * Used for consolidating duplicate items
   * @param newItem Item to check for duplicates
   * @returns Index of duplicate item or -1 if not found
   */
  private findDuplicateItem(newItem: any): number {
    return this.cart.findIndex((cartItem) => {
      if (cartItem.id !== newItem.id) return false;
      if(cartItem.additional_info !== newItem.additional_info) return false;
      if (!newItem.selectedChoices || newItem.selectedChoices.length === 0) return true;
      if (cartItem.selectedChoices.length !== newItem.selectedChoices.length) return false;
      const cartChoices = [...cartItem.selectedChoices].sort((a, b) => a.id - b.id);
      const newChoices = [...newItem.selectedChoices].sort((a, b) => a.id - b.id);

      return cartChoices.every((choice, index) => {
        const newChoice = newChoices[index];
        return choice.id === newChoice.id && choice.quantity === newChoice.quantity;
      });
    });
  }

  createProductModal(product: MenuItemDto, viewContainerRef: ViewContainerRef, quantity?: number, cartItemId?: string, choices?: any[], additional_info?: string): NgxSmartModalComponent {
    this.savedScrollPosition = window.scrollY;

    if (product.choices && product.choices.length > 0) {
      return this.createProductWithChoiceModal(viewContainerRef).setData({ product, quantity, cartItemId, choices, additional_info });
    }

    return this.createProductDefaultModal(viewContainerRef).setData({ product, quantity, cartItemId, additional_info });
  }

  private createProductWithChoiceModal(viewContainerRef: ViewContainerRef): NgxSmartModalComponent {
    return this.ngxSmartModalService.create(
      'product-modal-with-choices',
      ProductModalChoicesComponent,
      viewContainerRef,
      {
        customClass: 'dialog-full-height dialog-product-choices',
      }
    );
  }

  private createProductDefaultModal(viewContainerRef: ViewContainerRef): NgxSmartModalComponent {
    return this.ngxSmartModalService.create(
      'product-modal-default',
      ProductModalDefaultComponent,
      viewContainerRef,
      {
        customClass: 'dialog-full-height dialog-product-default',
      }
    );
  }
}
